import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './AIChatbot.css';

const AIChatbot = ({ category, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const messagesEndRef = useRef(null);
  const [isMinimized, setIsMinimized] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Load suggested questions
    loadSuggestedQuestions();
    
    // Welcome message
    setMessages([{
      type: 'bot',
      text: `Γεια σου! 👋 Είμαι ο AI βοηθός του be50.gr. Πώς μπορώ να σε βοηθήσω σήμερα;`,
      timestamp: new Date()
    }]);
  }, [category]);

  const loadSuggestedQuestions = async () => {
    try {
      const response = await axios.get(
        `/api/ai/questions/${category || 'general'}`
      );
      setSuggestedQuestions(response.data.questions);
    } catch (error) {
      console.error('Error loading questions:', error);
    }
  };

  const sendMessage = async (messageText) => {
    const userMessage = messageText || inputMessage;
    if (!userMessage.trim()) return;

    // Add user message
    const newUserMessage = {
      type: 'user',
      text: userMessage,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newUserMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      const response = await axios.post('/api/ai/chat', {
        message: userMessage,
        category: category,
        conversationHistory: conversationHistory
      });

      // Simulate typing delay
      setTimeout(() => {
        const botMessage = {
          type: 'bot',
          text: response.data.message,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMessage]);
        setConversationHistory(response.data.conversationHistory);
        setIsTyping(false);
      }, 1000);

    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        type: 'bot',
        text: 'Συγγνώμη, κάτι πήγε στραβά. Προσπάθησε ξανά.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (isMinimized) {
    return (
      <div className="chatbot-minimized" onClick={() => setIsMinimized(false)}>
        <div className="robot-avatar-mini">
          <div className="robot-head">
            <div className="robot-antenna"></div>
            <div className="robot-eyes">
              <div className="robot-eye"></div>
              <div className="robot-eye"></div>
            </div>
          </div>
        </div>
        <span className="chat-badge">💬</span>
      </div>
    );
  }

  return (
    <div className="chatbot-container">
      <div className="chatbot-header">
        <div className="robot-avatar">
          <div className="robot-head">
            <div className="robot-antenna"></div>
            <div className="robot-eyes">
              <div className="robot-eye"></div>
              <div className="robot-eye"></div>
            </div>
            <div className="robot-mouth"></div>
          </div>
        </div>
        <div className="chatbot-title">
          <h3>AI Βοηθός Freelancher</h3>
          <span className="status-indicator">● Online</span>
        </div>
        <div className="chatbot-controls">
          <button className="minimize-btn" onClick={() => setIsMinimized(true)}>−</button>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
      </div>

      <div className="chatbot-messages">
        {messages.map((msg, index) => (
          <div key={index} className={`message ${msg.type}`}>
            {msg.type === 'bot' && (
              <div className="robot-avatar-small">
                <div className="robot-head-small">
                  <div className="robot-eyes-small">
                    <div className="robot-eye-small"></div>
                    <div className="robot-eye-small"></div>
                  </div>
                </div>
              </div>
            )}
            <div className="message-bubble">
              {msg.text}
              <span className="message-time">
                {msg.timestamp.toLocaleTimeString('el-GR', { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </span>
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="message bot">
            <div className="robot-avatar-small">
              <div className="robot-head-small">
                <div className="robot-eyes-small">
                  <div className="robot-eye-small"></div>
                  <div className="robot-eye-small"></div>
                </div>
              </div>
            </div>
            <div className="message-bubble typing-indicator">
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {suggestedQuestions.length > 0 && messages.length <= 1 && (
        <div className="suggested-questions">
          <p>Συχνές ερωτήσεις:</p>
          {suggestedQuestions.map((question, index) => (
            <button
              key={index}
              className="suggested-question"
              onClick={() => sendMessage(question)}
            >
              {question}
            </button>
          ))}
        </div>
      )}

      <div className="chatbot-input">
        <textarea
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Γράψε το μήνυμά σου..."
          rows="1"
        />
        <button 
          className="send-btn"
          onClick={() => sendMessage()}
          disabled={!inputMessage.trim()}
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default AIChatbot;
